/**
 * useAuth.ts
 * Hook React pour gérer l'authentification Supabase
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../services/SupabaseService';
import { authService } from '../services/AuthService';
import { ensureProfile } from '../services/ProfileService';
import { invalidateCache } from '../services/RoleService';
import { logger } from '../utils/ProfessionalLogger';
import type { User, Session } from '@supabase/supabase-js';

export interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Charger l'utilisateur et la session au montage
  useEffect(() => {
    let mounted = true;

    // P3 FIX CRITIQUE : filet de sécurité. Si `getSession()` de Supabase hang
    // (bug connu avec React.StrictMode en dev → deadlock du lock interne,
    // ou réseau lent), `loading` resterait `true` à vie et l'app afficherait
    // "Chargement…" indéfiniment. Ce timeout garantit qu'on débloque toujours.
    const safetyTimeout = setTimeout(() => {
      if (mounted) {
        logger.warn('[useAuth] getSession() trop lent — déblocage via timeout de sécurité');
        setLoading(false);
      }
    }, 3000);

    // Récupérer la session initiale (avec catch pour ne jamais rester bloqué)
    authService
      .getSession()
      .then(session => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        clearTimeout(safetyTimeout);
      })
      .catch(error => {
        logger.error('[useAuth] Échec getSession initial:', error);
        if (mounted) {
          setLoading(false);
          clearTimeout(safetyTimeout);
        }
      });

    // Écouter les changements d'authentification (seulement si Supabase est configuré)
    if (isSupabaseConfigured() && supabase) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        // P3 : débloquer l'UI IMMÉDIATEMENT — ne jamais attendre ensureProfile.
        setLoading(false);
        clearTimeout(safetyTimeout);

        // PR-05 : S'assurer que le profil existe après login/signup (en tâche de fond,
        // non bloquant pour le rendu — l'await précédent bloquait le spinner).
        if (
          session?.user &&
          // P3 : 'SIGNED_UP' n'existe plus dans Supabase v2 — confondu avec 'INITIAL_SESSION'.
          (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')
        ) {
          ensureProfile(session.user.id).catch(error => {
            logger.warn('Erreur lors de la création du profil', { error }, 'useAuth');
          });
        }

        // Invalider le cache admin si déconnexion
        if (event === 'SIGNED_OUT') {
          invalidateCache();
        }
      });

      return () => {
        mounted = false;
        clearTimeout(safetyTimeout);
        subscription.unsubscribe();
      };
    } else {
      // Si Supabase n'est pas configuré, on considère qu'on est en mode hors ligne
      setLoading(false);
      clearTimeout(safetyTimeout);
      return () => {
        mounted = false;
        clearTimeout(safetyTimeout);
      };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await authService.signIn(email, password);
      if (result.error) {
        return { error: new Error(result.error.message) };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
    setLoading(true);
    try {
      const result = await authService.signUp(email, password, fullName);
      if (result.error) {
        return { error: new Error(result.error.message) };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      const result = await authService.signOut();
      if (result.error) {
        return { error: new Error(result.error.message) };
      }
      setUser(null);
      setSession(null);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const result = await authService.signInWithGoogle();
      if (result.error) {
        return { error: new Error(result.error.message) };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const result = await authService.resetPassword(email);
      if (result.error) {
        return { error: new Error(result.error.message) };
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }, []);

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    resetPassword,
  };
}
